import { Cell, Select, TextInput } from '@zeiss/beyond-online-react';
import {
  Box,
  Grid,
  LocationSelect,
  RequiredField,
  Section,
  SectionHeadline,
  SectionText,
} from '@zeiss/lib-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useWizardForm } from 'src/modules/Invitations/InvitationCreate/form';
import { getEmailFieldSchema } from 'src/modules/Invitations/InvitationCreate/schema';
import type { StepComponent } from 'src/modules/Invitations/InvitationCreate/types';
import { getSupportedLanguageOptions } from 'src/modules/Invitations/InvitationCreate/utils';

export const StepRecipientFields: StepComponent = ({ displayFieldError }) => {
  const { i18n, t } = useTranslation(['common', 'invitations']);
  const form = useWizardForm();

  const emailFieldSchema = useMemo(
    () => getEmailFieldSchema(t),
    [i18n.language, t],
  );

  const languageOptions = useMemo(
    () => [
      { key: '', label: '' },
      ...getSupportedLanguageOptions(i18n.language),
    ],
    [i18n.language],
  );

  return (
    <>
      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline
            headline={t('invitations:title.personalInformation')}
            size="s"
          />
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box mode="static" spacing="l">
            <Grid>
              <Cell.Basic colSpan={[4, 6, 6]}>
                <form.Field name="step2.firstName">
                  {(field) => (
                    <TextInput
                      autoComplete="given-name"
                      id={field.name}
                      label={t('common:firstName')}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      value={field.state.value}
                    />
                  )}
                </form.Field>
              </Cell.Basic>

              <Cell.Basic colSpan={[4, 6, 6]}>
                <form.Field name="step2.lastName">
                  {(field) => (
                    <TextInput
                      autoComplete="family-name"
                      id={field.name}
                      label={t('common:lastName')}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      value={field.state.value}
                    />
                  )}
                </form.Field>
              </Cell.Basic>

              <Cell.Basic colSpan={[4, 6, 12]}>
                <form.Field
                  name="step2.email"
                  validators={{
                    onBlur: emailFieldSchema,
                    onChange: emailFieldSchema,
                    onSubmit: emailFieldSchema,
                  }}
                >
                  {(field) => (
                    <RequiredField>
                      {/* Keep the raw value (type=text) so whitespace-sensitive validation can reject it. */}
                      <TextInput
                        autoComplete="email"
                        error={displayFieldError(field)}
                        id={field.name}
                        inputMode="email"
                        label={t('common:glossary.email')}
                        name={field.name}
                        onBlur={field.handleBlur}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        type="text"
                        value={field.state.value}
                      />
                    </RequiredField>
                  )}
                </form.Field>
              </Cell.Basic>

              <Cell.Basic colSpan={[4, 6, 6]}>
                <form.Field name="step2.location">
                  {(field) => (
                    <LocationSelect
                      label={t('common:label.location')}
                      language={i18n.language.split('-')[0]}
                      onChange={field.handleChange}
                      value={field.state.value || ''}
                    />
                  )}
                </form.Field>
              </Cell.Basic>
            </Grid>
          </Box>
        </Cell.Basic>
      </Section>

      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline headline={t('common:label.language')} size="s" />
          <SectionText>{t('invitations:text.language')}</SectionText>
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box mode="static" spacing="l">
            <Grid>
              <Cell.Basic colSpan={[4, 6, 6]}>
                <form.Field name="step2.locale">
                  {(field) => (
                    <Select
                      label={t('common:label.language')}
                      onChange={field.handleChange}
                      options={languageOptions}
                      value={field.state.value}
                    />
                  )}
                </form.Field>
              </Cell.Basic>
            </Grid>
          </Box>
        </Cell.Basic>
      </Section>
    </>
  );
};
